<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="value => emit('update:isVisible', value)" :isClosable="false">
    <template #header>
      <h2 class="global-h2">Your contact details</h2>
    </template>

    <template #content>
      <div class="creator-contact-details">
        <div class="creator-contact-details__intro">Needed for reservations and any last-minute coordination if you're chosen for this creator visit.</div>

        <!-- Full name -->
        <div class="creator-contact-details__field">
          <div class="global-h4 creator-contact-details__title">Your full name</div>
          <div class="creator-contact-details__description">For any activity or lodging reservations and payment.</div>
          <input class="global-text-input" style="max-width: 320px" type="text" v-model.trim="fullName" placeholder="Your Name" />
          <div v-if="isFullNameErrorVisible && !fullName" class="global-error-string creator-contact-details__error">Please enter your full name</div>
        </div>
        <!-- / Full name -->

        <!-- Phone number -->
        <div class="creator-contact-details__field">
          <div class="global-h4 creator-contact-details__title">Your phone number</div>
          <div class="creator-contact-details__description">
            Your number may be used for any last-minute coordination or critical notifications.<br />
            We'll never share your number with anyone besides the destination you're working with.
          </div>

          <div v-if="isPhoneNumberVerified" class="creator-contact-details__description">
            Your phone number is: <MaskedPhoneNumber :phoneNumber="userProfileStore.getViewingUserProfile.phoneNumber" />
          </div>
          <SrpButton size="tiny" :fill="isPhoneNumberVerified ? 'text' : 'solid'" @click="isPhoneNumberVerificationModalVisible = true">
            {{ isPhoneNumberVerified ? "Change phone number" : "Provide your phone number" }}
          </SrpButton>
          <div v-if="isPhoneNumberErrorVisible && !isPhoneNumberVerified" class="global-error-string creator-contact-details__error">Please verify your phone number</div>
        </div>
        <!-- / Phone number -->

        <div v-if="isSaveErrorVisible" class="global-error-string">Something went wrong saving your details. Please retry or email support@shrpa.com</div>
      </div>
    </template>

    <template #footer>
      <SrpButton @click="emit('update:isVisible', false)" color="gray" fill="outlined" style="margin-right: 10px" :isDisabled="isSaving">Cancel</SrpButton>
      <SrpButton @click="onContinue" :isDisabled="isSaving">
        <template v-if="isSaving" #icon><LoadingIcon :size="16" /></template>
        Continue
      </SrpButton>
    </template>
  </SrpModal>

  <SrpModal v-model:isVisible="isPhoneNumberVerificationModalVisible" :zIndex="400" size="small">
    <template #content>
      <PhoneNumberVerificationForm
        v-if="isPhoneNumberVerificationModalVisible"
        @phoneVerified="isPhoneNumberVerificationModalVisible = false"
        @verificationCanceled="isPhoneNumberVerificationModalVisible = false"
      />
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import ProfileRepo from "@logic/ProfileRepo";

// Components
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import MaskedPhoneNumber from "@components/MaskedPhoneNumber.vue";
import PhoneNumberVerificationForm from "@components/PhoneNumberVerificationForm.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";

// Stores
import { useUserProfileStore } from "@stores/userProfileStore";
const userProfileStore = useUserProfileStore();

// Collects the creator's full name and a text-verified phone number before they apply for (or accept) a Creator Visit.
// Both are saved to their profile, so this only needs to be shown while one of them is missing.
const props = withDefaults(
  defineProps<{
    isVisible: boolean;
  }>(),
  {
    isVisible: false,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  // Both fields are set and saved
  (e: "completed"): void;
}>();

// Full name ==================================================================
// Edited locally and only written to the profile on Continue, so cancelling doesn't leave a half-typed name behind
const fullName = ref<string>("");
const isFullNameErrorVisible = ref<boolean>(false);

// Phone number ===============================================================
// PhoneNumberVerificationForm updates the profile itself once the code is confirmed
const isPhoneNumberVerified = computed<boolean>(() => userProfileStore.getViewingUserProfile?.phoneNumberIsVerified === true);
const isPhoneNumberVerificationModalVisible = ref<boolean>(false);
const isPhoneNumberErrorVisible = ref<boolean>(false);

// Continue ===================================================================
const isSaving = ref<boolean>(false);
const isSaveErrorVisible = ref<boolean>(false);

// Reset when opened
watch(
  () => props.isVisible,
  isVisible => {
    if (!isVisible) return;
    fullName.value = userProfileStore.getViewingUserProfile?.collabPreferences?.fullName?.trim() ?? "";
    isFullNameErrorVisible.value = false;
    isPhoneNumberErrorVisible.value = false;
    isSaveErrorVisible.value = false;
  },
  { immediate: true }
);

async function onContinue() {
  isFullNameErrorVisible.value = !fullName.value;
  isPhoneNumberErrorVisible.value = !isPhoneNumberVerified.value;
  if (!fullName.value || !isPhoneNumberVerified.value) return;

  const profile = userProfileStore.getViewingUserProfile;
  if (fullName.value !== profile.collabPreferences?.fullName) {
    isSaving.value = true;
    isSaveErrorVisible.value = false;
    try {
      await ProfileRepo.saveFullName(profile.sherpaId, fullName.value);
      if (profile.collabPreferences) profile.collabPreferences.fullName = fullName.value;
    } catch (e) {
      isSaveErrorVisible.value = true;
      return;
    } finally {
      isSaving.value = false;
    }
  }

  emit("completed");
}
</script>

<style scoped lang="scss">
.creator-contact-details {
  color: #5b5b5b;
  font: 300 14px/19px sans-serif;

  &__intro {
    margin-bottom: 22px;
  }

  &__field {
    margin-bottom: 28px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__title {
    margin-bottom: 4px;
  }

  &__description {
    margin-bottom: 13px;
  }

  &__error {
    margin-top: 8px;
  }
}
</style>
